import { observer } from 'mobx-react';
import React, { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStoreContext } from '@stores/rootStore';
import { LFEChart } from '@app/_components/lf-graph/LFEChart';
import { ECharts, BarSeriesOption, TooltipComponentFormatterCallbackParams } from 'echarts';
import { convertSqmToAreaByUnit } from '@farmai-shared/LFUtils';
import { thousandSeparator } from '@services/utils';
import { Legend } from '@app/_components/lf-graph/Legend';
import { MockPlantingGraph } from './Mock';
import { PlantingResponse } from '@services/responseTypes';
import { GraphTooltip } from '@app/_components/weatherModal/month/graphUtils';
export const plantingGraphColors = {
  created: '#BFC7E1',
  planted: '#1CC449',
  ready_to_harvest: '#D6B10A',
  harvested: '#A86861'
};
const keysToIgnore = ['label', 'not_planted', 'year', 'week'];
export const PlantingGraph: FC<{
  data: PlantingResponse['series'] | undefined;
  hasDataToDisplay?: boolean;
  mock?: boolean;
}> = observer(({
  data,
  hasDataToDisplay = true,
  mock = false
}) => {
  const [chartInstance, setChartInstance] = useState<ECharts | null>(null);
  const {
    userStore
  } = useContext(RootStoreContext);
  const {
    t
  } = useTranslation();
  const areaUnit = userStore.user.settings.area_unit;
  const lang = userStore.user.settings.lang;
  const onChartInit = chart => setChartInstance(chart);
  if (!hasDataToDisplay) {
    return <MockPlantingGraph title={t('noGraphData')} />;
  }
  const groupDataByType = (data?: PlantingResponse['series']) => {
    if (!data) return;
    const groupedData = {};
    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (!keysToIgnore.includes(key)) {
          if (!groupedData[key]) {
            groupedData[key] = [];
          }
          groupedData[key].push(convertSqmToAreaByUnit(item[key], areaUnit, {
            computed: true
          }));
        }
      });
    });
    const result = Object.keys(groupedData).map(key => ({
      type: 'bar',
      name: key,
      data: groupedData[key],
      itemStyle: {
        color: plantingGraphColors[key]
      }
    }));
    return result;
  };
  const toggleLegendSelection = (chartInstance: ECharts | null, label: string) => {
    if (!chartInstance) return;
    chartInstance.dispatchAction({
      type: 'legendToggleSelect',
      name: label
    });
  };
  const series = groupDataByType(data);
  const toolTipFormatter = (params: TooltipComponentFormatterCallbackParams) => {
    if (!Array.isArray(params)) return '';
    let output = '<div class="w-full flex flex-col gap-y-2">';
    output += `<p>${params[0].name}</p>`;
    params.forEach(param => {
      output += `<div class="flex">
              <div>${param.marker}</div>
              <div class=" w-full flex justify-between">
                <p class="font-light pr-2">${t(param.seriesName || '')}</p>
                <p class="min-w-fit whitespace-nowrap">${thousandSeparator(param.value as number)} ${t(areaUnit)}</p>
              </div>
            </div>`;
    });
    return output + '</div>';
  };
  const labels = {
    created: 'plantingGraph_created',
    planted: 'plantingGraph_planted',
    ready_to_harvest: 'plantingGraph_ready',
    harvested: 'plantingGraph_harvested'
  };
  const legendData = Object.keys(plantingGraphColors).map(key => ({
    label: key,
    svg: <rect key={key} // key is necessary for eslint
    width={16} height={16} rx={4} fill={plantingGraphColors[key]}></rect>,
    extra: <div className="print:hidden">
        <GraphTooltip label={labels[key]} />
      </div>
  }));
  return <div className="flex h-full flex-col">
      <div className={`
          h-144
          lg:h-full
        `}>
        <LFEChart series={series as BarSeriesOption} onChartInit={onChartInit} tooltip={{
        show: !mock,
        trigger: 'axis',
        formatter: toolTipFormatter,
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        textStyle: {
          color: 'rgb(255,255,255)'
        }
      }} legend={{
        show: false // legend object must exist for custom dispatch action
      }} dataZoom={[{
        type: 'slider',
        show: !mock,
        start: 0,
        end: 100,
        filterMode: 'empty',
        showDetail: false
      }]} grid={{
        top: '4%',
        left: '1%',
        right: '1%',
        containLabel: true
      }} xAxis={{
        type: 'category',
        data: data?.map(x => `${t(x.label)}\n${lang === 'th' ? parseInt(x.year, 10) + 543 : x.year}`)
      }} yAxis={{
        type: 'value',
        axisLabel: {
          formatter: `{value} ${t(areaUnit)}`
        }
      }} />
      </div>

      <div className="print:mt-64">
        <Legend data={legendData} event={toggleLegendSelection} chartInstance={chartInstance} />
      </div>
    </div>;
});